import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Stack, Typography } from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import axios from "axios";

import apiRoutes from "../../../../../constants/api-routes";
import messageLevels from "../../../../../constants/message-levels";
import messages from "../../../../../constants/messages";
import useAuthHeader from "../../../../../hooks/useAuthHeader";
import useToastAlert from "../../../../../hooks/useToastAlert";
import { useScimWizardContext } from "../../../../../providers/ScimWizardProvider";
import { selectOrganizationId } from "../../../../../redux/userSlice";
import CoAppCreateGroupOrRoleModal from "../../../../global/components/modals/CoAppCreateGroupOrRoleModal";
import CoAppDestructiveConfirmationModal from "../../../../global/components/modals/CoAppDestructiveConfirmationModal";
import CoAppEditNameModal from "../../../../global/components/modals/CoAppEditNameModal";
import { CoAppInvertedActionButton } from "../../../../global/styled/global.styled";
import { getStep5RoleColumns } from "../columns/step5-role-columns";
import ScimWizardDataGrid from "../ScimWizardDataGrid";
import { ScimWizardDataGridStepContainer } from "../styled/scim-wizard.styled";

export default function Step5() {
	const { identityProviderName, mappedRoles, setMappedRoles, validateRoleMappings, roleMappingStatus } = useScimWizardContext();

	const authHeader = useAuthHeader();
	const { handleToastAlert } = useToastAlert();
	const organizationId = useSelector(selectOrganizationId);
	const apiRef = useGridApiRef();
	const [roles, setRoles] = useState([]);
	const [idpGroups, setIdpGroups] = useState([]);
	const [roleName, setRoleName] = useState("");
	const [roleNameError, setRoleNameError] = useState();
	const [createRoleDialogOpen, setCreateRoleDialogOpen] = useState(false);
	const [editRoleDialogOpen, setEditRoleDialogOpen] = useState(false);
	const [roleBeingEdited, setRoleBeingEdited] = useState({});
	const [deleteRoleDialogOpen, setDeleteRoleDialogOpen] = useState(false);
	const [roleBeingDeleted, setRoleBeingDeleted] = useState({});

	const toggleCreateRoleDialog = () => {
		setCreateRoleDialogOpen(!createRoleDialogOpen);
		setRoleNameError("");
		setRoleName("");
	};

	const toggleEditRoleDialog = (role = {}) => {
		setEditRoleDialogOpen(!editRoleDialogOpen);
		setRoleNameError("");
		setRoleName("");
		setRoleBeingEdited(role);
	};

	const toggleDeleteRoleDialog = (role = {}) => {
		setDeleteRoleDialogOpen(!deleteRoleDialogOpen);
		setRoleBeingDeleted(role);
	};

	const handleRoleNameChange = (e) => {
		setRoleName(e.target.value);
		setRoleNameError("");
	};

	const createNewRole = () => {
		if (!roleName) {
			setRoleNameError("Required *");
			return;
		}

		let role = { name: roleName };

		axios.post(apiRoutes.createRole, role, { headers: authHeader })
			.then(() => {
				handleToastAlert(messageLevels.SUCCESS, messages.ROLE_CREATED_SUCCESS_MSG);
				toggleCreateRoleDialog();
				initializeOrganizationRoles();
			})
			.catch((err) => {
				if (err.response.data.message.includes("already exists")) {
					setRoleNameError(messages.RESOURCE_ALREADY_EXISTS_ERROR_MSG("Role"));
					return;
				}
				toggleCreateRoleDialog();
				handleToastAlert(messageLevels.ERROR, messages.ROLE_CREATION_ERROR_MSG);
			});
	};

	const handleRoleNameChangeSubmit = (e) => {
		if (!roleName) return;

		if (roleName === roleBeingEdited.name) {
			toggleEditRoleDialog();
			return;
		}

		axios.put(apiRoutes.updateRole(roleBeingEdited.id), { name: roleName }, { headers: authHeader })
			.then(() => {
				initializeOrganizationRoles();
				toggleEditRoleDialog();
			})
			.catch((e) => {
				console.log(e);
				if (e.response.data.message.includes("already exists")) {
					setRoleNameError(messages.RESOURCE_NAME_ALREADY_EXISTS_ERROR_MSG("Role"));
					return;
				}
				toggleEditRoleDialog();
				handleToastAlert(messageLevels.ERROR, messages.ROLE_NAME_UPDATE_ERROR_MSG);
			});
	};
	const deleteRole = () => {
		axios.delete(apiRoutes.deleteRole(roleBeingDeleted.id), { headers: authHeader })
			.then(() => {
				handleToastAlert(messageLevels.INFO, messages.ROLE_DELETION_SUCCESS_MSG(false));
				initializeOrganizationRoles();
				toggleDeleteRoleDialog();
			})
			.catch((err) => {
				console.log(err);
				handleToastAlert(messageLevels.ERROR, messages.ROLE_DELETION_ERROR_MSG);
				toggleDeleteRoleDialog();
			});
	};

	const initializeOrganizationRoles = () => {
		axios.get(apiRoutes.getRoles, { headers: authHeader })
			.then((res) => {
				let protectedRole = res.data.find(role => role.isProtected);
				let protectedRoleIndex = res.data.indexOf(protectedRole);
				res.data.splice(protectedRoleIndex, 1);
				res.data.unshift(protectedRole);
				setRoles(res.data);
				let tempMappedRoles = { ...mappedRoles };
				for (let role of res.data) {
					if (Object.keys(roleBeingEdited).length > 0 && role.id === roleBeingEdited.id) {
						tempMappedRoles[`${role.name}`] = tempMappedRoles[`${roleBeingEdited.name}`];
					} else {
						tempMappedRoles[`${role.name}`] = !tempMappedRoles[`${role.name}`] ? [] : tempMappedRoles[`${role.name}`];
					}
				}
				if (Object.keys(roleBeingDeleted).length > 0) {
					delete tempMappedRoles[`${roleBeingDeleted.name}`];
				}
				if (Object.keys(roleBeingEdited).length > 0) {
					delete tempMappedRoles[`${roleBeingEdited.name}`];
				}
				setMappedRoles(tempMappedRoles);
			})
			.catch(err => {
				console.log(err);
			});
	};

	const initializeExternalGroups = async () => {
		axios.get(apiRoutes.getExternalGroups(organizationId), {
			headers: authHeader
		})
			.then((res) => {
				setIdpGroups(res.data);
			})
			.catch((err) => {
				console.log(err);
			});
	};

	const columns = useMemo(() => (
		getStep5RoleColumns(
			identityProviderName,
			idpGroups,
			mappedRoles,
			setMappedRoles,
			apiRef,
			validateRoleMappings,
			roleMappingStatus,
			initializeExternalGroups,
			setIdpGroups,
			toggleEditRoleDialog,
			toggleDeleteRoleDialog
		)
	), [identityProviderName, idpGroups, mappedRoles, apiRef, roleMappingStatus]);

	useEffect(() => {
		initializeOrganizationRoles();
	}, []);

	return (
		<ScimWizardDataGridStepContainer>
			<Stack direction="row" spacing={10} sx={{ paddingLeft: "55px" }}>
				<Typography variant="h3_5">Map Roles</Typography>
				<CoAppInvertedActionButton onClick={toggleCreateRoleDialog}>
					Add Role
				</CoAppInvertedActionButton>
			</Stack>
			<ScimWizardDataGrid
				apiRef={apiRef}
				rows={roles}
				columns={columns}
			/>
			<CoAppCreateGroupOrRoleModal
				cancelClickHandler={toggleCreateRoleDialog}
				changeHandler={handleRoleNameChange}
				dialogOpen={createRoleDialogOpen}
				nameError={roleNameError}
				confirmClickHandler={createNewRole}
				isRole={true}
			/>
			<CoAppEditNameModal
				dialogOpen={editRoleDialogOpen}
				dialogTitle="Edit role name"
				changeHandler={handleRoleNameChange}
				placeholderText={roleBeingEdited.name}
				confirmClickHandler={handleRoleNameChangeSubmit}
				cancelClickHandler={toggleEditRoleDialog}
				actionText="Save"
				editNameError={roleNameError}
				setEditNameError={setRoleNameError}
			/>
			<CoAppDestructiveConfirmationModal
				dialogOpen={deleteRoleDialogOpen}
				dialogTitle={`Delete ${roleBeingDeleted.name} role?`}
				dialogMessage={messages.DELETION_CONFIRMATION_MSG(roleBeingDeleted.name, "role")}
				confirmClickHandler={deleteRole}
				cancelClickHandler={toggleDeleteRoleDialog}
				actionText="Delete"
			/>
		</ScimWizardDataGridStepContainer>
	);
}